import { useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DateDropdown from "@/components/DateDropdown";

const ESTADOS_MX = ["Aguascalientes","Baja California","Baja California Sur","Campeche","Chiapas","Chihuahua","Ciudad de México","Coahuila","Colima","Durango","Estado de México","Guanajuato","Guerrero","Hidalgo","Jalisco","Michoacán","Morelos","Nayarit","Nuevo León","Oaxaca","Puebla","Querétaro","Quintana Roo","San Luis Potosí","Sinaloa","Sonora","Tabasco","Tamaulipas","Tlaxcala","Veracruz","Yucatán","Zacatecas"];
const RELACIONES = ["Madre","Padre","Cónyuge","Hijo/a","Hermano/a","Abuelo/a","Otro"];
const TIPOS_SANGRE = ["O+","O-","A+","A-","B+","B-","AB+","AB-","No sé"];
const SEXOS = [{ value: "M", label: "Hombre" }, { value: "F", label: "Mujer" }];

const empty = () => ({
  nombre:"", relacion:"", telefono:"", telefono_alt:"",
  calle:"", numero_ext:"", numero_int:"",
  colonia:"", municipio:"", estado:"", codigo_postal:"", referencias:"",
  // medical
  fecha_nacimiento: "", sexo: "", tipo_sangre: "",
  hipertension: false, diabetes: false, cardiopatia: false,
  asma: false, epilepsia: false, embarazada: false,
  alergias: "", medicamentos: "", diagnostico: "",
  contacto_emergencia_nombre: "", contacto_emergencia_telefono: "",
});

export default function BeneficiariosEditor({ user, onSaved, maxAllowed = 4 }) {
  const initial = Array.isArray(user?.beneficiarios) && user.beneficiarios.length ? user.beneficiarios : [empty()];
  const [list, setList] = useState(initial);
  const [saving, setSaving] = useState(false);

  const setField = (idx, field, value) => {
    const arr = [...list]; arr[idx] = { ...arr[idx], [field]: value }; setList(arr);
  };
  const add = () => { if (list.length < maxAllowed) setList([...list, empty()]); };
  const remove = (idx) => { if (list.length > 1) setList(list.filter((_, i) => i !== idx)); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/me/beneficiarios", { beneficiarios: list });
      toast.success("Beneficiarios actualizados");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="beneficiarios-editor">
      {list.map((b, idx) => (
        <div key={idx} className="beneficiario-card" data-testid={`benef-card-${idx}`}>
          <div className="beneficiario-header">
            <strong>Beneficiario {idx + 1}</strong>
            {list.length > 1 && (
              <button type="button" onClick={() => remove(idx)} className="link-danger" data-testid={`benef-remove-${idx}`}>
                <i className="fas fa-trash" /> Quitar
              </button>
            )}
          </div>
          <div className="form-grid-2">
            <div className="form-group"><label>Nombre Completo *</label>
              <input type="text" required value={b.nombre} onChange={(e) => setField(idx, "nombre", e.target.value)} data-testid={`benef-${idx}-nombre`} /></div>
            <div className="form-group"><label>Parentesco *</label>
              <select required value={b.relacion} onChange={(e) => setField(idx, "relacion", e.target.value)} data-testid={`benef-${idx}-relacion`}>
                <option value="">Selecciona</option>{RELACIONES.map((r) => <option key={r}>{r}</option>)}
              </select></div>
            <div className="form-group"><label>Teléfono MX *</label>
              <input type="tel" required placeholder="+52..." value={b.telefono} onChange={(e) => setField(idx, "telefono", e.target.value)} data-testid={`benef-${idx}-tel`} /></div>
            <div className="form-group"><label>Tel. adicional</label>
              <input type="tel" value={b.telefono_alt} onChange={(e) => setField(idx, "telefono_alt", e.target.value)} /></div>
            <div className="form-group full-width"><label>Calle *</label>
              <input type="text" required value={b.calle} onChange={(e) => setField(idx, "calle", e.target.value)} /></div>
            <div className="form-group"><label>Núm. Ext *</label>
              <input type="text" required value={b.numero_ext} onChange={(e) => setField(idx, "numero_ext", e.target.value)} /></div>
            <div className="form-group"><label>Núm. Int</label>
              <input type="text" value={b.numero_int} onChange={(e) => setField(idx, "numero_int", e.target.value)} /></div>
            <div className="form-group"><label>Colonia *</label>
              <input type="text" required value={b.colonia} onChange={(e) => setField(idx, "colonia", e.target.value)} /></div>
            <div className="form-group"><label>Municipio *</label>
              <input type="text" required value={b.municipio} onChange={(e) => setField(idx, "municipio", e.target.value)} /></div>
            <div className="form-group"><label>Estado *</label>
              <select required value={b.estado} onChange={(e) => setField(idx, "estado", e.target.value)}>
                <option value="">Selecciona</option>{ESTADOS_MX.map((s) => <option key={s}>{s}</option>)}
              </select></div>
            <div className="form-group"><label>Código Postal *</label>
              <input type="text" required maxLength={5} value={b.codigo_postal} onChange={(e) => setField(idx, "codigo_postal", e.target.value)} /></div>
            <div className="form-group full-width"><label>Referencias</label>
              <input type="text" placeholder="Ej. casa azul, entre calles..." value={b.referencias} onChange={(e) => setField(idx, "referencias", e.target.value)} /></div>
          </div>

          <h4 className="form-subsection" style={{ color: "#B91C1C" }}>
            <i className="fas fa-notes-medical" /> Ficha médica (importante en emergencias)
          </h4>
          <div className="form-grid-2">
            <div className="form-group"><label>Fecha de nacimiento</label>
              <DateDropdown value={b.fecha_nacimiento || ""} onChange={(v) => setField(idx, "fecha_nacimiento", v)} testId={`benef-${idx}-fn`} /></div>
            <div className="form-group"><label>Sexo</label>
              <select value={b.sexo || ""} onChange={(e) => setField(idx, "sexo", e.target.value)} data-testid={`benef-${idx}-sexo`}>
                <option value="">Selecciona</option>{SEXOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select></div>
            <div className="form-group"><label>Tipo de sangre</label>
              <select value={b.tipo_sangre || ""} onChange={(e) => setField(idx, "tipo_sangre", e.target.value)} data-testid={`benef-${idx}-sangre`}>
                <option value="">Selecciona</option>{TIPOS_SANGRE.map((s) => <option key={s}>{s}</option>)}
              </select></div>
            <div className="form-group full-width">
              <label style={{ marginBottom: 8 }}>Condiciones médicas (marca las que apliquen)</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {[
                  { k: "hipertension", l: "Hipertensión" },
                  { k: "diabetes", l: "Diabetes" },
                  { k: "cardiopatia", l: "Cardiopatía" },
                  { k: "asma", l: "Asma" },
                  { k: "epilepsia", l: "Epilepsia" },
                  { k: "embarazada", l: "Embarazo" },
                ].map(({ k, l }) => (
                  <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 500 }}>
                    <input type="checkbox" checked={!!b[k]} onChange={(e) => setField(idx, k, e.target.checked)} data-testid={`benef-${idx}-${k}`} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group full-width"><label>Alergias conocidas</label>
              <input type="text" placeholder="Ej. penicilina, mariscos..." value={b.alergias || ""} onChange={(e) => setField(idx, "alergias", e.target.value)} /></div>
            <div className="form-group full-width"><label>Medicamentos que toma actualmente</label>
              <input type="text" placeholder="Ej. metformina 500mg / losartán 50mg" value={b.medicamentos || ""} onChange={(e) => setField(idx, "medicamentos", e.target.value)} /></div>
            <div className="form-group full-width"><label>Diagnóstico médico u observaciones</label>
              <textarea rows={2} placeholder="Ej. paciente con insuficiencia renal crónica controlada" value={b.diagnostico || ""} onChange={(e) => setField(idx, "diagnostico", e.target.value)} /></div>
            <div className="form-group"><label>Contacto de emergencia (nombre)</label>
              <input type="text" value={b.contacto_emergencia_nombre || ""} onChange={(e) => setField(idx, "contacto_emergencia_nombre", e.target.value)} /></div>
            <div className="form-group"><label>Contacto de emergencia (teléfono)</label>
              <input type="tel" value={b.contacto_emergencia_telefono || ""} onChange={(e) => setField(idx, "contacto_emergencia_telefono", e.target.value)} /></div>
          </div>
        </div>
      ))}
      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        {list.length < maxAllowed && (
          <button type="button" onClick={add} className="btn btn-secondary" data-testid="benef-add">
            <i className="fas fa-plus" /> Agregar beneficiario ({list.length}/{maxAllowed})
          </button>
        )}
        <button type="button" onClick={save} disabled={saving} className="btn btn-primary" data-testid="benef-save">
          {saving ? "Guardando..." : "Guardar beneficiarios"}
        </button>
      </div>
    </div>
  );
}
